import React from 'react';
import { Button, Group, Paper, Text } from '@mantine/core';
import { IconAlertTriangle, IconRoute, IconX } from '@tabler/icons-react';

export interface GanttCriticalPathStripProps {
  /** Tasks on the critical path (excludes ancestor-only WBS rows). */
  criticalCount: number;
  /** Critical-path tasks past planned finish and incomplete (not chart “critical red”). */
  overdueCount: number;
  onTurnOff: () => void;
}

/** Full-width strip above the Gantt when Critical path focus is on. */
export function GanttCriticalPathStrip({
  criticalCount,
  overdueCount,
  onTurnOff,
}: GanttCriticalPathStripProps) {
  const hasOverdue = overdueCount > 0;
  const accent = hasOverdue
    ? 'var(--mantine-color-red-6)'
    : 'var(--mantine-color-blue-6)';
  const colorName = hasOverdue ? 'red' : 'blue';
  const Icon = hasOverdue ? IconAlertTriangle : IconRoute;

  const countLabel =
    criticalCount === 0
      ? 'No critical tasks'
      : `${criticalCount.toLocaleString()} critical task${criticalCount === 1 ? '' : 's'}`;

  const healthLabel =
    criticalCount === 0
      ? ''
      : hasOverdue
        ? ` · ${overdueCount.toLocaleString()} overdue`
        : ' · on schedule';

  return (
    <Paper
      withBorder
      radius="md"
      px="sm"
      py={6}
      role="status"
      aria-live="polite"
      style={{
        flexShrink: 0,
        borderColor: `color-mix(in srgb, ${accent} 45%, var(--mantine-color-default-border))`,
        background: `color-mix(in srgb, ${accent} 12%, var(--mantine-color-body))`,
      }}
    >
      <Group justify="space-between" wrap="nowrap" gap="sm" align="center">
        <Group gap={8} wrap="wrap" align="center" style={{ minWidth: 0 }}>
          <Group gap={6} wrap="nowrap" align="center">
            <Icon
              size={14}
              stroke={1.75}
              style={{ color: accent, flexShrink: 0 }}
              aria-hidden
            />
            <Text size="xs" fw={700} c={colorName}>
              Critical path
            </Text>
          </Group>
          <Text size="xs" fw={600} c="dimmed">
            {countLabel}
            {healthLabel}
          </Text>
        </Group>
        <Button
          size="compact-xs"
          variant="subtle"
          color={colorName}
          leftSection={<IconX size={12} stroke={1.75} />}
          onClick={onTurnOff}
          style={{ flexShrink: 0 }}
        >
          Turn off
        </Button>
      </Group>
    </Paper>
  );
}
